import React from 'react';
import * as THREE from 'three';
import { CAD_TO_THREEJS_MATRIX, CadOrigin, parseFullGeometryNearOrigin } from './geometryLoader';
import { buildTunnelSceneObject, isSceneSource, parseTunnelScene } from '../TunnelProfile/tunnelScene';
import { cadToThreeJsPositionWithOffset } from '../../Scenes/scene-utils';

interface TunnelProfileMeshProps {
  fileData: string | undefined;
  guid: string | undefined;
  groupPosition: THREE.Vector3;
  groupScale: number;
  color?: number;
  opacity?: number;
  sourceName?: string;
}

const DEFAULT_TUNNEL_COLOR = 0x6bd0ff;
const DEFAULT_TUNNEL_OPACITY = 0.35;

interface BuiltTunnel {
  object: THREE.Object3D;
  meshMaterials: THREE.MeshPhongMaterial[];
  origin: CadOrigin;
}

const disposeBuilt = (built: BuiltTunnel | null) => {
  if (!built) return;
  const materials = new Set<THREE.Material>();
  built.object.traverse((child) => {
    const obj = child as THREE.Mesh & THREE.Line;
    obj.geometry?.dispose();
    const mat = obj.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((m) => materials.add(m));
    else if (mat) materials.add(mat);
  });
  materials.forEach((m) => m.dispose());
};

const applyOpacity = (built: BuiltTunnel, opacity: number) => {
  const transparent = opacity < 1;
  // Indexed loop with a local const: avoids both no-param-reassign (forEach) and no-restricted-syntax (for..of).
  for (let i = 0; i < built.meshMaterials.length; i += 1) {
    const material = built.meshMaterials[i];
    material.opacity = opacity;
    material.transparent = transparent;
    material.depthWrite = opacity >= 1;
    material.needsUpdate = true;
  }
};

// OBJ/DXF path: one shared colour for all geometry (no per-entity colour info available).
const buildFromGeometry = (fileData: string, color: number, sourceName?: string): BuiltTunnel | null => {
  const local = parseFullGeometryNearOrigin(fileData, sourceName ?? 'tunnel.obj');
  if (!local) return null;
  const { object, origin } = local;

  const meshMaterial = new THREE.MeshPhongMaterial({ color, side: THREE.DoubleSide });
  const lineMaterial = new THREE.LineBasicMaterial({ color });

  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.geometry.applyMatrix4(CAD_TO_THREEJS_MATRIX);
      mesh.geometry.computeVertexNormals();
      mesh.material = meshMaterial;
      return;
    }
    const line = child as THREE.Line;
    if (line.isLine) {
      line.geometry.applyMatrix4(CAD_TO_THREEJS_MATRIX);
      line.material = lineMaterial;
    }
  });

  return { object, meshMaterials: [meshMaterial], origin };
};

const buildTunnel = (fileData: string, color: number, opacity: number, sourceName?: string): BuiltTunnel | null => {
  let built: BuiltTunnel | null;

  if (isSceneSource(sourceName)) {
    // Rich per-entity scene JSON (DUF): real Deswik colours per solid/string.
    const scene = parseTunnelScene(fileData);
    if (!scene || scene.entities.length === 0) return null;
    built = buildTunnelSceneObject(scene, opacity);
  } else {
    built = buildFromGeometry(fileData, color, sourceName);
  }

  if (built) applyOpacity(built, opacity);
  return built;
};

const TunnelProfileMesh = ({
  fileData,
  guid,
  groupPosition,
  groupScale,
  color = DEFAULT_TUNNEL_COLOR,
  opacity = DEFAULT_TUNNEL_OPACITY,
  sourceName,
}: TunnelProfileMeshProps) => {
  const [built, setBuilt] = React.useState<BuiltTunnel | null>(null);
  const lastGuidRef = React.useRef<string | undefined>();

  // Same transform as the camera framing and the clipping box: (cad + offset) * scale.
  const position = React.useMemo(
    () => (built ? new THREE.Vector3(...cadToThreeJsPositionWithOffset(built.origin, groupPosition, groupScale)) : undefined),
    [built, groupPosition, groupScale],
  );

  // Rebuild (re-parse) only when the source geometry changes — not on opacity changes.
  React.useEffect(() => {
    if (!fileData) {
      setBuilt((prev) => { disposeBuilt(prev); return null; });
      lastGuidRef.current = undefined;
      return;
    }
    if (guid === lastGuidRef.current) return;
    lastGuidRef.current = guid;

    setBuilt((prev) => {
      disposeBuilt(prev);
      return buildTunnel(fileData, color, opacity, sourceName);
    });
    // opacity intentionally excluded from deps — opacity changes are handled by the effect below
    // without re-parsing the geometry.
  }, [fileData, guid, sourceName, color]);

  React.useEffect(() => {
    if (built) applyOpacity(built, opacity);
  }, [built, opacity]);

  React.useEffect(() => () => { disposeBuilt(built); }, [built]);

  if (!built) return null;

  return (
    <group key={guid ?? 'tunnel'} position={position} scale={groupScale}>
      <primitive object={built.object} />
    </group>
  );
};

export default TunnelProfileMesh;
